/**
 * 统一日志管理工具
 * 支持按环境控制日志输出，提供调试模式开关和敏感信息脱敏
 */

export type LogLevel = "debug" | "log" | "info" | "warn" | "error";

export interface LoggerConfig {
  level: LogLevel;
  enableDebug: boolean;
  showTimestamp: boolean;
  showSource: boolean;
}

/**
 * Logger 类
 * 提供分级日志管理、性能监控和敏感信息脱敏功能
 */
export class Logger {
  private config: LoggerConfig;
  private timers: Map<string, number> = new Map();

  constructor(config?: Partial<LoggerConfig>) {
    const isDevelopment = import.meta.env.MODE === "development";
    const isDebugMode =
      typeof localStorage !== "undefined" && localStorage.getItem("debug_mode") === "true";

    this.config = {
      level: import.meta.env.VITE_LOG_LEVEL || (isDevelopment ? "debug" : "error"),
      enableDebug: isDevelopment || isDebugMode,
      showTimestamp: true,
      showSource: true,
      ...config,
    };
  }

  /**
   * 获取当前时间戳字符串
   */
  private getTimestamp(): string {
    const now = new Date();
    return now.toLocaleString("zh-CN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  }

  /**
   * 格式化日志消息
   */
  private formatMessage(level: LogLevel, source: string | undefined, ...args: any[]): any[] {
    const parts: any[] = [];

    if (this.config.showTimestamp) {
      parts.push(`[${this.getTimestamp()}]`);
    }

    if (this.config.showSource && source) {
      parts.push(`[${source}]`);
    }

    parts.push(...args);
    return parts;
  }

  /**
   * 判断是否应该输出日志
   */
  private shouldLog(level: LogLevel): boolean {
    // 错误和警告始终显示
    if (level === "error" || level === "warn") {
      return true;
    }

    // 开发环境或调试模式下显示所有日志
    if (this.config.enableDebug) {
      return true;
    }

    // 生产环境只显示错误和警告
    return false;
  }

  /**
   * 错误日志（始终显示）
   */
  error(...args: any[]): void {
    if (this.shouldLog("error")) {
      console.error(...this.formatMessage("error", undefined, ...args));
    }
  }

  /**
   * 警告日志
   */
  warn(...args: any[]): void {
    if (this.shouldLog("warn")) {
      console.warn(...this.formatMessage("warn", undefined, ...args));
    }
  }

  /**
   * 信息日志
   */
  info(...args: any[]): void {
    if (this.shouldLog("info")) {
      console.info(...this.formatMessage("info", undefined, ...args));
    }
  }

  /**
   * 一般日志
   */
  log(...args: any[]): void {
    if (this.shouldLog("log")) {
      console.log(...this.formatMessage("log", undefined, ...args));
    }
  }

  /**
   * 调试日志（仅调试模式）
   */
  debug(...args: any[]): void {
    if (this.config.enableDebug) {
      console.log(...this.formatMessage("debug", undefined, ...args));
    }
  }

  /**
   * 性能监控 - 开始计时
   */
  time(label: string): void {
    if (this.config.enableDebug) {
      this.timers.set(label, Date.now());
    }
  }

  /**
   * 性能监控 - 结束计时并输出耗时
   */
  timeEnd(label: string): void {
    if (this.config.enableDebug) {
      const startTime = this.timers.get(label);
      if (startTime) {
        const elapsed = Date.now() - startTime;
        console.log(...this.formatMessage("log", undefined, `${label}: ${elapsed}ms`));
        this.timers.delete(label);
      }
    }
  }

  /**
   * 用户ID脱敏
   * 示例: "user_12345" -> "us***45"
   */
  static maskUserId(userId: string): string {
    if (!userId || userId.length <= 3) {
      return "***";
    }
    const len = userId.length;
    return userId.substring(0, 2) + "***" + userId.substring(len - 2);
  }

  /**
   * Token脱敏
   * 示例: "abcdef123456" -> "abc...456"
   */
  static maskToken(token: string): string {
    if (!token || token.length <= 6) {
      return "***";
    }
    return token.substring(0, 3) + "..." + token.substring(token.length - 3);
  }

  /**
   * 权限列表脱敏（只显示数量）
   * 示例: ["admin", "read", "write"] -> "3个权限"
   */
  static maskPermissions(perms: string[]): string {
    if (!perms || perms.length === 0) {
      return "无权限";
    }
    return `${perms.length}个权限`;
  }
}

// 导出单例实例
export const logger = new Logger();

// 导出脱敏工具函数（方便直接使用）
export const { maskUserId, maskToken, maskPermissions } = Logger;
